import { useCallback, useMemo } from 'react';

import { logger } from 'lib/logger';
import type { WorkflowEdgeData, WorkflowGraph, WorkflowNode } from 'model/xyflowMapping';
import {
  addControlEdge,
  addNodeAfter as addNodeAfterMutation,
  addNodeAtPosition as addNodeAtPositionMutation,
  deleteNode as deleteNodeMutation,
  duplicateNode as duplicateNodeMutation,
  removeStepOutputRefs,
  renameStep as renameStepMutation,
} from 'model/xyflow/mutations';
import type { GraphUpdater } from './useWorkflowGraphState';

export interface UseEditorActionsArgs {
  /**
   * `apply` from `useWorkflowGraphState` — runs the updater inside an
   * undo-stack push + dirty-flip.
   */
  apply: (updater: GraphUpdater) => void;
  /**
   * Setter for the page-level "selected node id" — moves alongside id
   * renames and clears when the selected node is deleted.
   */
  setSelectedNodeId: (update: string | null | ((current: string | null) => string | null)) => void;
}

export interface UseEditorActionsResult {
  renameStep: (oldId: string, newId: string) => void;
  addNodeAfter: (sourceNodeId: string, actionId: string) => void;
  addNodeAtPosition: (
    actionId: string,
    position: { x: number; y: number },
    parentStepId?: string,
  ) => void;
  duplicateNode: (nodeId: string) => void;
  deleteNode: (nodeId: string) => void;
  cleanupAfterDelete: (nodeIds: string[]) => void;
  addEdge: (sourceId: string, targetId: string) => void;
  deleteEdge: (edgeId: string) => void;
  updateEdgeData: (edgeId: string, partial: Partial<WorkflowEdgeData>) => void;
  updateNode: (updated: WorkflowNode) => void;
  setInputBinding: (
    targetId: string,
    fieldName: string,
    sourceId: string,
    outputName: string | undefined,
  ) => void;
}

/**
 * Named mutation handlers — each wraps a pure mutation function in
 * `apply()`. Lives in a hook so the page composition stays compact and
 * each handler has a stable reference for memoised children.
 *
 * Two of the handlers (`renameStep`, `deleteNode`) also poke the
 * page-level `selectedNodeId` because deleting / renaming the currently
 * selected node would otherwise leave a dangling selection.
 */
export const useEditorActions = ({
  apply,
  setSelectedNodeId,
}: UseEditorActionsArgs): UseEditorActionsResult => {
  const renameStep = useCallback(
    (oldId: string, newId: string) => {
      apply((current: WorkflowGraph) => renameStepMutation(current, oldId, newId));
      setSelectedNodeId((current) => (current === oldId ? newId.trim() : current));
    },
    [apply, setSelectedNodeId],
  );

  const addNodeAfter = useCallback(
    (sourceNodeId: string, actionId: string) => {
      apply((current: WorkflowGraph) => addNodeAfterMutation(current, sourceNodeId, actionId));
    },
    [apply],
  );

  const addNodeAtPosition = useCallback(
    (actionId: string, position: { x: number; y: number }, parentStepId?: string) => {
      apply((current: WorkflowGraph) =>
        addNodeAtPositionMutation(current, actionId, position, parentStepId),
      );
    },
    [apply],
  );

  const duplicateNode = useCallback(
    (nodeId: string) => {
      apply((current: WorkflowGraph) => duplicateNodeMutation(current, nodeId));
    },
    [apply],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      apply((current: WorkflowGraph) => deleteNodeMutation(current, nodeId));
      setSelectedNodeId((current) => (current === nodeId ? null : current));
    },
    [apply, setSelectedNodeId],
  );

  const cleanupAfterDelete = useCallback(
    (nodeIds: string[]) => {
      setSelectedNodeId((current) => (current && nodeIds.includes(current) ? null : current));
      const deletedSet = new Set(nodeIds);
      apply((current: WorkflowGraph) => ({
        ...current,
        nodes: current.nodes.map((n) => removeStepOutputRefs(n, deletedSet)),
      }));
    },
    [apply, setSelectedNodeId],
  );

  const addEdge = useCallback(
    (sourceId: string, targetId: string) => {
      apply((current: WorkflowGraph) => addControlEdge(current, sourceId, targetId));
    },
    [apply],
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      apply((current: WorkflowGraph) => ({
        ...current,
        edges: current.edges.filter((e) => e.id !== edgeId),
      }));
    },
    [apply],
  );

  const updateEdgeData = useCallback(
    (edgeId: string, partial: Partial<WorkflowEdgeData>) => {
      apply((current: WorkflowGraph) => ({
        ...current,
        edges: current.edges.map((e) => {
          if (e.id !== edgeId) return e;
          // `e.data` is technically optional on the base xyflow Edge but the
          // workflow graph always populates it via xyflowMapping; merge with
          // the existing kind preserved. If we ever hit the fallback in
          // production it means a control path produced an edge without
          // .data — log it so the invariant violation is visible instead of
          // silently being mislabelled as 'control'.
          let existing: WorkflowEdgeData;
          if (e.data === undefined) {
            logger.warn(
              `updateEdgeData: edge ${e.id} has no data; assuming kind='control' as fallback`,
            );
            existing = { kind: 'control' };
          } else {
            existing = e.data;
          }
          const data: WorkflowEdgeData = {
            ...existing,
            ...partial,
            kind: partial.kind ?? existing.kind,
          };
          return { ...e, data, label: data.routeKey || undefined };
        }),
      }));
    },
    [apply],
  );

  const updateNode = useCallback(
    (updated: WorkflowNode) => {
      apply((current: WorkflowGraph) => ({
        ...current,
        nodes: current.nodes.map((n) => (n.id === updated.id ? updated : n)),
      }));
    },
    [apply],
  );

  const setInputBinding = useCallback(
    (targetId: string, fieldName: string, sourceId: string, outputName: string | undefined) => {
      apply((current: WorkflowGraph) => ({
        ...current,
        nodes: current.nodes.map((n) => {
          if (n.id !== targetId) return n;
          return {
            ...n,
            data: {
              ...n.data,
              input: {
                ...(n.data.input ?? {}),
                [fieldName]: {
                  kind: 'StepOutput' as const,
                  stepId: sourceId,
                  target: outputName ?? '',
                },
              },
            },
          };
        }),
      }));
    },
    [apply],
  );

  return useMemo(
    () => ({
      renameStep,
      addNodeAfter,
      addNodeAtPosition,
      duplicateNode,
      deleteNode,
      cleanupAfterDelete,
      addEdge,
      deleteEdge,
      updateEdgeData,
      updateNode,
      setInputBinding,
    }),
    [
      renameStep,
      addNodeAfter,
      addNodeAtPosition,
      duplicateNode,
      deleteNode,
      cleanupAfterDelete,
      addEdge,
      deleteEdge,
      updateEdgeData,
      updateNode,
      setInputBinding,
    ],
  );
};
